import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { postRequestRegister } from "@/api/user/home";
import { toEnglishNumber, toPersianNumber } from "@/hooks/useThousandsSeparator";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { zodResolver } from "@hookform/resolvers/zod";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
    CircularProgress, FormControl, FormControlLabel, IconButton,
    InputAdornment, Radio, RadioGroup, TextField,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";

const formSchema = z.object({
    mobile: z.string().min(1, "شماره تماس الزامی است"),
    nID: z.string().min(1, "کدملی الزامی است"),
    pass: z.string().min(6, "رمز عبور باید حداقل 6 کاراکتر باشد"),
    passRepeat: z.string().min(1, "تکرار رمز عبور الزامی است"),
    referralCode: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface FormRegisterProps {
    setIsRegister: Dispatch<SetStateAction<boolean>>;
    setIsOtpRegister: Dispatch<SetStateAction<boolean>>;
    setStatePhoneNumberRegister: Dispatch<SetStateAction<any>>;
    setIndRegister: Dispatch<SetStateAction<any>>;
    setStateNidRegister: Dispatch<SetStateAction<any>>;
    setStatePassRegister: Dispatch<SetStateAction<any>>;
    setStateResetPassRegister: Dispatch<SetStateAction<any>>;
    referralCode?: string | null;
}

const FormRegister = ({
                          setIsRegister, setIsOtpRegister,
                          setStatePhoneNumberRegister, setIndRegister,
                          setStateNidRegister, setStatePassRegister, setStateResetPassRegister,
                          referralCode,
                      }: FormRegisterProps) => {
    const { register, handleSubmit, formState: { errors, isSubmitting }, watch, reset, setValue } =
        useForm<FormData>({ resolver: zodResolver(formSchema) });

    const [valuePhoneNumber, setValuePhoneNumber] = React.useState("");
    const [valueNidNumber, setValueNidNumber] = React.useState("");
    const [valuePassNumber, setValuePassNumber] = React.useState("");
    const [valueRessetPass, setValueRessetPass] = React.useState("");
    const [valueReferralCode, setValueReferralCode] = React.useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showRepeatPassword, setShowRepeatPassword] = useState(false);

    const { mutate: fetchRequestRegister, isPending } = useMutation({
        mutationFn: postRequestRegister,
        onSuccess: () => {
            setIsRegister(false);
            setIsOtpRegister(true);
        },
        onError: (error: AxiosError<{ message: string }>) => {
            const msg = error?.response?.data?.message;
            toast.error(msg);
        },
    });

    useEffect(() => { reset({ nID: "", pass: "" }); }, [reset]);

    useEffect(() => {
        if (referralCode) {
            setValueReferralCode(referralCode);
            setValue("referralCode", referralCode);
        }
    }, [referralCode, setValue]);

    const handleClickShowPassword = () => setShowPassword((p) => !p);
    const handleClickShowRepeatPassword = () => setShowRepeatPassword((p) => !p);

    const password = watch("pass");

    const onSubmit = async (data: FormData) => {
        setStatePhoneNumberRegister(valuePhoneNumber);
        setStateNidRegister(valueNidNumber);
        setStatePassRegister(valuePassNumber);
        setStateResetPassRegister(valueRessetPass);
        setIndRegister(data.nID);

        const requestBody: any = {
            mobile: toEnglishNumber(valuePhoneNumber),
            nID: toEnglishNumber(valueNidNumber),
            pass: toEnglishNumber(data.pass),
            passRepeat: toEnglishNumber(data.passRepeat),
        };
        if (valueReferralCode && valueReferralCode.trim() !== "") {
            requestBody.referralCode = valueReferralCode;
        }
        fetchRequestRegister(requestBody);
    };

    useEffect(() => { reset(); }, []);

    const submitDisabled = isSubmitting || !acceptTerms;

    return (
        <form className="w-full flex flex-col justify-center" onSubmit={handleSubmit(onSubmit)} style={{ width: "100%", marginTop: "0" }}>
            <StackKit justifyContent={"center"} alignItems={"center"} gap={2} sx={{ width: { xs: '100%', sm: '100%', md: '80%' } }}>

                <StackKit alignItems={"start"} justifyContent={"start"} sx={{ paddingTop: "50px" }}>
                    <TypoGraphyKit variant="subtitle1">ثبت نام در {toPersianNumber(20)} کارت </TypoGraphyKit>
                    <TypoGraphyKit variant="body1">
                        جهت ثبت نام در {toPersianNumber(20)} کارت اطلاعات خواسته شده را وارد نمایید
                    </TypoGraphyKit>
                </StackKit>

                {/* mobile */}
                <StackKit sx={{ width: "70%" }} gap={0.5}>
                    <TextField
                        {...register("mobile")}
                        placeholder="شماره تماس"
                        error={!!errors.mobile}
                        sx={{ background: "linear-gradient(90deg, #444444 -35.13%, #000000 126.71%)", borderRadius: "10px", width: "100%" }}
                        value={valuePhoneNumber ? toPersianNumber(valuePhoneNumber) : ""}
                        onChange={(e) => {
                            const rawValue = toEnglishNumber(e.target.value).replace(/[^0-9]/g, "");
                            if (rawValue.length <= 11) setValuePhoneNumber(rawValue);
                        }}
                    />
                    {errors.mobile && (
                        <TypoGraphyKit variant="body2" sx={{ color: "#ff6b6b", fontSize: "12px" }}>
                            {errors.mobile.message}
                        </TypoGraphyKit>
                    )}
                </StackKit>

                {/* nID */}
                <StackKit sx={{ width: "70%" }} gap={0.5}>
                    <TextField
                        {...register("nID")}
                        placeholder="کد ملی"
                        error={!!errors.nID}
                        sx={{ background: "linear-gradient(90deg, #444444 -35.13%, #000000 126.71%)", borderRadius: "10px", width: "100%" }}
                        value={valueNidNumber ? toPersianNumber(valueNidNumber) : ""}
                        onChange={(e) => {
                            const rawValue = toEnglishNumber(e.target.value).replace(/[^0-9]/g, "");
                            if (rawValue.length <= 10) setValueNidNumber(rawValue);
                        }}
                    />
                    {errors.nID && (
                        <TypoGraphyKit variant="body2" sx={{ color: "#ff6b6b", fontSize: "12px" }}>
                            {errors.nID.message}
                        </TypoGraphyKit>
                    )}
                </StackKit>

                {/* pass */}
                <StackKit sx={{ width: "70%" }} gap={0.5}>
                    <TextField
                        {...register("pass")}
                        type={showPassword ? "text" : "password"}
                        placeholder="رمز عبور"
                        error={!!errors.pass}
                        sx={{ background: "linear-gradient(90deg, #444444 -35.13%, #000000 126.71%)", borderRadius: "10px", width: "100%" }}
                        autoComplete="new-password"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleClickShowPassword} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {errors.pass && (
                        <TypoGraphyKit variant="body2" sx={{ color: "#ff6b6b", fontSize: "12px" }}>
                            {errors.pass.message}
                        </TypoGraphyKit>
                    )}
                </StackKit>

                {/* passRepeat */}
                <StackKit sx={{ width: "70%" }} gap={0.5}>
                    <TextField
                        {...register("passRepeat", {
                            required: "تکرار رمز عبور الزامی است",
                            validate: (value) => value === password || "رمز عبور مطابقت ندارد",
                        })}
                        sx={{ background: "linear-gradient(90deg, #444444 -35.13%, #000000 126.71%)", borderRadius: "10px", width: "100%" }}
                        type={showRepeatPassword ? "text" : "password"}
                        placeholder="تکرار رمز عبور"
                        error={!!errors.passRepeat}
                        autoComplete="new-password"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleClickShowRepeatPassword} edge="end">
                                        {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {errors.passRepeat && (
                        <TypoGraphyKit variant="body2" sx={{ color: "#ff6b6b", fontSize: "12px" }}>
                            {errors.passRepeat.message}
                        </TypoGraphyKit>
                    )}
                </StackKit>

                {/* referral */}
                <StackKit sx={{ width: "70%" }} gap={0.5}>
                    <TypoGraphyKit textAlign={'right'} marginTop={'10px'} variant="body1">
                        کد معرف (اختیاری)
                    </TypoGraphyKit>
                    <TextField
                        {...register("referralCode")}
                        placeholder="کد معرف (اختیاری)"
                        sx={{
                            background: "linear-gradient(90deg, #444444 -35.13%, #000000 126.71%)",
                            borderRadius: "10px", width: "100%",
                            "& .MuiInputLabel-root": { color: "#fff", fontSize: "14px", fontWeight: 500, padding: "0 5px", borderRadius: "0.5rem", marginRight: "10px" },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#ffcc00" },
                        }}
                        value={valueReferralCode}
                        onChange={(e) => {
                            setValueReferralCode(e.target.value);
                            setValue("referralCode", e.target.value);
                        }}
                        disabled={!!referralCode}
                    />
                </StackKit>

                {/* terms */}
                <StackKit sx={{ width: "70%" }} gap={0.5}>
                    <FormControl>
                        <RadioGroup value={acceptTerms ? "accepted" : ""} onChange={(e) => setAcceptTerms(e.target.value === "accepted")}>
                            <FormControlLabel
                                value="accepted"
                                control={<Radio sx={{ color: "#363636", "&.Mui-checked": { color: "#363636" } }} />}
                                label={<TypoGraphyKit variant="body2" sx={{ fontSize: "14px" }}>شرایط و قوانین 20 کارت را خوانده و می پذیرم.</TypoGraphyKit>}
                            />
                        </RadioGroup>
                    </FormControl>
                    <StackKit sx={{ width: "100%" }} direction={"row"} alignItems={"center"} justifyContent={"center"}>
                        <TypoGraphyKit variant="body1" sx={{ color: "#CE0000", fontWeight: 700, fontSize: "16px" }}>
                            {errorMessage}
                        </TypoGraphyKit>
                    </StackKit>
                </StackKit>

                {/* submit */}
                <StackKit sx={{ width: "70%" }}>
                    <CustomButton
                        type="submit"
                        disabled={submitDisabled}
                        sx={{
                            background: submitDisabled
                                ? "gray"
                                : "linear-gradient(90deg, #CF2791 7.87%, #FFCD34 120.53%)",
                        }}
                    >
                        <TypoGraphyKit variant="body1">
                            {isPending ? <CircularProgress sx={{ color: "#fff" }} size={24} /> : "ادامه"}
                        </TypoGraphyKit>
                    </CustomButton>
                </StackKit>

                <StackKit alignItems={"center"} sx={{ width: "40%" }}>
                    <button
                        type="button"
                        style={{ background: "none", border: "none", color: "#3687FF", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                        onClick={() => setIsRegister(false)}
                    >
                        حساب کاربری دارید؟ وارد شوید
                    </button>
                </StackKit>
            </StackKit>
        </form>
    );
};

export default FormRegister;